/**
 * Shared hook for profile list options (user genders, educations, occupations, etc.).
 * Used by BasicProfileForm (edit) and DoctorProfile (view) for consistent display.
 *
 * Profile gender comes from Managed Lists → User Genders (user_gender_masters),
 * NOT from Remedy Genders.
 */
import { useState, useEffect } from 'react';
import { listManagementApi } from '../api/admin/ListManagementApi';

export type SelectOption = { value: string; label: string };

interface ListItem {
  name: string;
  value?: string | null;
  code?: string | null;
}

function toOption(item: ListItem, useCode = false): SelectOption {
  const value = useCode
    ? item.code || item.value || item.name
    : item.value || item.name;
  return { value: String(value), label: item.name };
}

/** Fallback if User Genders API is empty / unavailable */
export const PROFILE_GENDER_OPTIONS_FALLBACK: SelectOption[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

/** @deprecated use PROFILE_GENDER_OPTIONS_FALLBACK or genderOptions from hook */
export const PROFILE_GENDER_OPTIONS = PROFILE_GENDER_OPTIONS_FALLBACK;

export function useProfileListOptions() {
  const [genderOptions, setGenderOptions] = useState<SelectOption[]>(
    PROFILE_GENDER_OPTIONS_FALLBACK,
  );
  const [educationOptions, setEducationOptions] = useState<SelectOption[]>([]);
  const [occupationOptions, setOccupationOptions] = useState<SelectOption[]>(
    [],
  );
  const [ethnicityOptions, setEthnicityOptions] = useState<SelectOption[]>([]);
  const [religionOptions, setReligionOptions] = useState<SelectOption[]>([]);
  const [languageOptions, setLanguageOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    const limit = { page: 1, limit: 200 };
    const fetch = async () => {
      try {
        const [genders, edu, occ, eth, rel, lang] = await Promise.all([
          listManagementApi.getUserGenders(limit),
          listManagementApi.getEducations(limit),
          listManagementApi.getOccupations(limit),
          listManagementApi.getEthnicities(limit),
          listManagementApi.getReligions(limit),
          listManagementApi.getLanguages(limit),
        ]);
        if (genders.success && genders.data?.data?.length) {
          setGenderOptions(
            (genders.data.data as ListItem[]).map((i) => toOption(i)),
          );
        }
        if (edu.success && edu.data?.data)
          setEducationOptions(
            (edu.data.data as ListItem[]).map((i) => toOption(i)),
          );
        if (occ.success && occ.data?.data)
          setOccupationOptions(
            (occ.data.data as ListItem[]).map((i) => toOption(i)),
          );
        if (eth.success && eth.data?.data)
          setEthnicityOptions(
            (eth.data.data as ListItem[]).map((i) => toOption(i)),
          );
        if (rel.success && rel.data?.data)
          setReligionOptions(
            (rel.data.data as ListItem[]).map((i) => toOption(i)),
          );
        if (lang.success && lang.data?.data)
          setLanguageOptions(
            (lang.data.data as ListItem[]).map((i) => toOption(i, true)),
          );
      } catch {
        // Keep fallbacks on error
      }
    };
    fetch();
  }, []);

  return {
    genderOptions,
    educationOptions,
    occupationOptions,
    ethnicityOptions,
    religionOptions,
    languageOptions,
  };
}

/** Format value for display when no option match (e.g. "gujarati" → "Gujarati"). */
function formatFallback(value: string): string {
  return value
    .split(/[-_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** Same as internal formatFallback — for read-only admin views. */
export function formatDisplayFallback(value: string): string {
  return formatFallback(value);
}

/** Resolve stored value to display label from options. */
export function resolveLabel(
  value: string | undefined,
  options: SelectOption[],
): string {
  if (!value) return '';
  const v = value.trim();
  if (!v) return '';
  // Exact value match
  const exact = options.find((o) => o.value === v);
  if (exact) return exact.label;
  // Case-insensitive value match
  const ciValue = options.find(
    (o) => o.value.toLowerCase() === v.toLowerCase(),
  );
  if (ciValue) return ciValue.label;
  // Match by label (e.g. backend stores "gujarati" but options have value "gu", label "Gujarati")
  const byLabel = options.find(
    (o) => o.label.toLowerCase() === v.toLowerCase(),
  );
  if (byLabel) return byLabel.label;
  return formatFallback(v);
}

/** Resolve comma-separated values to display labels. */
export function resolveLabels(
  value: string | undefined,
  options: SelectOption[],
): string {
  if (!value) return '';
  return value
    .split(',')
    .map((v) => resolveLabel(v.trim(), options))
    .filter(Boolean)
    .join(', ');
}

/** Marital status options (no list API, backend-validated values). */
export const MARITAL_STATUS_OPTIONS: SelectOption[] = [
  { value: 'never_married', label: 'Never married (Single)' },
  { value: 'married', label: 'Married' },
  { value: 'separated', label: 'Separated' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'cohabit', label: 'Co-habit' },
  { value: 'refuse', label: 'Prefer not to say' },
];
